import { useState } from "react";
import {
  getWheelRaffleErrorMessage,
  useGetRaffleParticipantsPageQuery,
  useGetRaffleWinnersPageQuery,
} from "entities/wheel-raffle";
import type { ApiError } from "shared/utils";
import { RAFFLE_POLLING_MS } from "./constants";
import { useClampPage } from "./useClampPage";

interface PageResult<Item> {
  data?: { items: Item[]; pages: number; per_page: number; total: number };
  isLoading: boolean;
  isFetching: boolean;
  error?: ApiError;
}

const toPageState = <Item>(
  result: PageResult<Item>,
  page: number,
  setPage: (page: number) => void,
) => ({
  items: result.data?.items ?? [],
  page,
  // Сколько строк на предыдущих страницах — для сквозной нумерации.
  offset: (page - 1) * (result.data?.per_page ?? 0),
  pages: result.data?.pages ?? 0,
  // Всего записей по всем страницам — для счётчика на вкладке.
  total: result.data?.total ?? 0,
  setPage,
  isLoading: result.isLoading,
  isFetching: result.isFetching,
  errorMessage: getWheelRaffleErrorMessage(result.error),
});

// Участники копятся, пока акция не разыграна, — обновляем по таймеру.
export const useParticipantsPage = (raffleId: number, isDrawn: boolean) => {
  const [page, setPage] = useState(1);
  const result = useGetRaffleParticipantsPageQuery(
    { raffleId, page },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: isDrawn ? 0 : RAFFLE_POLLING_MS,
    },
  );
  useClampPage(page, result.data?.pages ?? 0, setPage);
  return toPageState(result, page, setPage);
};

// До розыгрыша бэк отвечает 409 — не запрашиваем.
export const useWinnersPage = (raffleId: number, isDrawn: boolean) => {
  const [page, setPage] = useState(1);
  const result = useGetRaffleWinnersPageQuery(
    { raffleId, page },
    { skip: !isDrawn, refetchOnMountOrArgChange: true },
  );
  useClampPage(page, result.data?.pages ?? 0, setPage);
  return toPageState(result, page, setPage);
};

export type PeoplePageState<Item> = ReturnType<typeof toPageState<Item>>;
